import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCachedAssessmentTemplates } from '../../hooks/useCachedData';
import useAssessment from '../../hooks/useAssessment';
import { FOCUS_LOOKUP, filterAssessmentsBySex } from '../../data/categoryDefinitions';
import './AssessmentSettings.css';

export default function AssessmentSettings({ focuses, sex }) {
    const navigate = useNavigate();
    const { assessmentTemplates } = useCachedAssessmentTemplates();
    const { loadSummary, hasExistingAssessment, getExistingDate } = useAssessment();

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    const relevantAssessments = useMemo(() => {
        if (!focuses || !assessmentTemplates) return [];
        const ids = new Set();
        for (const fId of focuses) {
            const focus = FOCUS_LOOKUP[fId];
            if (focus) {
                for (const aid of focus.assessment_ids) {
                    if (assessmentTemplates[aid]) ids.add(aid);
                }
            }
        }
        return filterAssessmentsBySex([...ids], sex).map(id => ({ id, ...assessmentTemplates[id] }));
    }, [focuses, assessmentTemplates, sex]);

    if (!focuses || focuses.length === 0 || !assessmentTemplates) return null;

    return (
        <div className="ns-root">
            <div className="ns-header">
                <svg className="ns-bell-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M9 12l2 2 4-4" />
                </svg>
                <h3 className="ns-title">Health Assessments</h3>
            </div>

            <div className="ns-card">
                {relevantAssessments.length === 0 ? (
                    <p className="as-empty">No assessments available for your focuses yet.</p>
                ) : (
                    <div className="as-list">
                        {relevantAssessments.map(a => {
                            const completed = hasExistingAssessment(a.id);
                            const date = getExistingDate(a.id);
                            return (
                                <div
                                    key={a.id}
                                    className="as-item"
                                    onClick={() => navigate(`/assessment/${a.id}`)}
                                >
                                    <div className="as-item-info">
                                        <span className="as-item-name">{a.display_name}</span>
                                        <span className="as-item-meta">
                                            {a.estimated_minutes} min
                                            {completed && (
                                                <span className="as-item-done">
                                                    {' '}· Completed {new Date(date).toLocaleDateString()}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
